import * as React from 'react';
import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { EditTargetDialog } from '../EditTargetDialog';
import * as Utilities from '../../ts/Utilities';
import { IWidget } from '../../../../pdc-platform/src/ts/Interfaces';
import { IValuesCollection, validate as validateTheme } from '../../ts/appTheme';
import { IParameterFieldBaseOptions } from '../../ts/ParameterFieldBase';
import { IDataEditValues } from '../dataEdit/Interfaces';
import { WidgetData } from '../../ts/WidgetData';
import { WidgetBase } from '../../ts/WidgetBase';

import {
    IHashCollection,
    IHashItem,
    IMultiWidgetFieldParamOptions,
    IMultiWidgetFieldValues,
    IMultiWidgetFieldWidgetDataOptions,
    MultiWidgetFieldTab
} from './MultiWidgetFieldTab';

export interface IMultiWidgetFieldOptions extends IParameterFieldBaseOptions {
    maxWidgets?: number;
    allowDelete?: boolean;
    sortable?: boolean;
    disabledTypes?: string[];
    tabIcons?: boolean;
    tabBackColors?: boolean;
    defaultTabBackgroundColor?: string;
    tabTextColors?: boolean;
    defaultTabTextColor?: string;
    showActivateByDefault?: boolean;
    tabsFieldName?: string;
    showHeaderTemplate?: boolean;

    parentTheme?(): IValuesCollection;
}

let _lastSelectedTab: number = undefined;

//EVG: Issue 86197.
function getSelectedTab(widgets: IWidget[]) {
    if (widgets == null || widgets.length === 0) {
        _lastSelectedTab = undefined;
        return undefined;
    }

    if (_lastSelectedTab != null) {
        const lastSelectedWidget = widgets.find((widget) => widget.id === _lastSelectedTab);

        if (lastSelectedWidget != null) {
            return _lastSelectedTab;
        }
    }

    return widgets[0].id;
}

function createHash(value: IMultiWidgetFieldValue, parentTheme: IValuesCollection, tabID: number, glob: IGlob) {
    const result = _.reduce<IWidget[], IHashCollection>(
        value,
        (hash, obj) => {
            hash[obj.id] = {
                data: WidgetData.getFromObj(obj, glob, true, parentTheme, tabID),
                value: obj
            };

            return hash;
        },
        {}
    );

    return result;
}

function putOptions(obj: IHashItem, options: IMultiWidgetFieldWidgetDataOptions) {
    if (obj.value.options == null) {
        obj.value.options = {};
    }

    if (obj.data.options == null) {
        obj.data.options = {};
    }

    Object.keys(options).forEach((name) => {
        obj.data.options[name] = obj.value.options[name] = options[name];
    });
}

function cleanOptions(obj: IHashItem, options: IMultiWidgetFieldWidgetDataOptions) {
    Object.keys(options).forEach((name) => {
        if (obj.value.options != null) {
            delete obj.value.options[name];

            if (_.isEmpty(obj.value.options)) {
                delete obj.value.options;
            }
        }

        if (obj.data.options != null) {
            delete obj.data.options[name];

            if (_.isEmpty(obj.data.options)) {
                delete obj.data.options;
            }
        }
    });
}

function showErrorsDialog(widget: WidgetBase, glob: IGlob) {
    if (widget == null || !widget.isInvalid) {
        return;
    }

    const params = widget.parameters;
    const errors = _.reduce(
        _.keys(params),
        (res, key) => {
            return res.concat(params[key].errors);
        },
        []
    );

    if (errors.length === 0) {
        return;
    }

    glob.DialogActions.createDialogConfirm(
        'Configuration is not valid',
        '',
        () => {},
        () => {},
        () => {},
        {
            content: (
                <div>
                    <strong>{'This widget has following errors:'}</strong>
                    <ul className="list-unstyled">
                        {_.map(errors, (str, i) => {
                            return <li key={i}>– {str}</li>;
                        })}
                    </ul>
                </div>
            ),
            confirmTitle: null,
            cancelTitle: null,
            closeTitle: 'OK'
        }
    );
}

function showSaveInCollectionDialog(data: WidgetData<IMultiWidgetFieldWidgetDataOptions>, glob: IGlob) {
    glob.DialogActions.createDialog(
        'Select a folder and name for Widget in collection',
        '',
        (dirid, name, overwrite) => {
            glob.AppActions.templateCreateSave(dirid, name, overwrite, data.getString());
        },
        () => {},
        () => {},
        'templateDirSelect',
        {
            selectTitle: 'Save here',
            rootId: 2,
            nameSelect: true,
            name: data.values[data.type].name || 'New Widget',
            overwrite: false,
            overwriteShow: true,
            allowSelectItem: true
        }
    );
}

function saveToLocalStorage(data: WidgetData<IMultiWidgetFieldWidgetDataOptions>, key: string, glob: IGlob) {
    const dataToSave = JSON.parse(JSON.stringify(data.getDataForSave())); // Clone nested objects.

    Utilities.jsonNormalize_DataSourceIDToName(dataToSave, glob);
    Utilities.jsonNormalize_ThemeIDToName(dataToSave, glob);

    const text = JSON.stringify(dataToSave, null, '\t');

    glob.AppStore.saveToLocalStorage(key, text);
    return text;
}

interface IMultiWidgetFieldState {
    value: IWidget[];
    selectedTab?: number;
    hash?: IHashCollection;
    selectedInternalTabs?: { [id: number]: number };
}

export type IMultiWidgetFieldValue = IWidget[];

export interface IMultiWidgetFieldProperties {
    value: IMultiWidgetFieldValue;
    values?: IMultiWidgetFieldValues;
    hideType?: boolean;
    parameterProps?: any[];
    paramOptions?: IMultiWidgetFieldParamOptions;
    noTabs?: boolean;
    tabIcons?: boolean;
    tabBackColors?: boolean;
    defaultTabBackgroundColor?: string;
    tabTextColors?: boolean;
    defaultTabTextColor?: string;
    showActivateByDefault?: boolean;
    hideTabs?: string[];
    showHeaderTemplate?: boolean;
    options: IMultiWidgetFieldOptions;
    glob: IGlob;

    onChange?(value: IMultiWidgetFieldValue): void;
}

export default class MultiWidgetField extends React.Component<IMultiWidgetFieldProperties, IMultiWidgetFieldState> {
    private references: {
        tabs?: HTMLUListElement;
    } = {};

    constructor(props) {
        super(props);

        const selectedTab = getSelectedTab(this.props.value);
        const appThemeStateValid = validateTheme();
        const parentTheme = this.props.options?.parentTheme();
        const tabID = this.props.options?.tabID;
        const themeStates = {};

        _.each(this.props.value, (obj) => {
            themeStates[obj.id] = appThemeStateValid;
        });

        const hash = createHash(this.props.value, parentTheme, tabID, this.props.glob);

        this.state = {
            value: this.props.value,
            hash: hash,
            selectedTab: selectedTab
        };
    }

    private startIndex = -1;
    private addColDef = null;
    private addColDefType = null;

    reorderStart = (e, ui) => {
        this.startIndex = $(ui.item).index();
    };

    reorderStop = (e, ui) => {
        if (this.startIndex == null) {
            return;
        }

        let startIndex = this.startIndex;
        let stopIndex = $(ui.item).index();

        delete this.startIndex;
        $(this.references.tabs).sortable('cancel');
        this.processReorder(startIndex, stopIndex);
    };

    processReorder = (start, stop) => {
        if (start === stop) {
            return;
        }

        let val = this.state.value.slice(0);
        let item = val.splice(start, 1);

        val.splice(stop, 0, item[0]);
        this.handleOnChange(val);
    };

    componentDidMount() {
        const sortable = this.props.options.sortable === false ? false : true;
        const tabs = this.references.tabs;

        if (sortable && tabs != null) {
            $(tabs).sortable({
                axis: 'x',
                forceHelperSize: true,
                items: '.widget-tab',
                opacity: 0.8,

                start: this.reorderStart,
                stop: this.reorderStop
            });
        }

        const defs = _.chain(this.state.hash).values().pluck('data').pluck('def').value();

        $.when.apply($, defs).done(() => {
            this.setState({ hash: this.state.hash });
        });
    }

    componentWillUnmount() {
        if (this.addColDef != null) {
            this.addColDef.reject();
        }

        if (this.addColDefType != null) {
            this.addColDefType.reject();
        }
    }

    componentWillReceiveProps(nextProps: IMultiWidgetFieldProperties) {
        if (nextProps.value !== this.props.value) {
            const parentTheme = nextProps.options?.parentTheme();
            const tabID = nextProps.options?.tabID;
            const hash = createHash(nextProps.value, parentTheme, tabID, nextProps.glob);
            const newState: IMultiWidgetFieldState = {
                value: nextProps.value,
                hash: hash
            };

            if (this.state.selectedTab == null) {
                newState.selectedTab = getSelectedTab(nextProps.value);
            }

            this.setState(newState);
        }
    }

    handleOnChange = (value: IWidget[], hash?, selectedTab?: number) => {
        const state: IMultiWidgetFieldState = {
            value: value
        };

        if (hash != null) {
            state.hash = hash;
        }

        if (selectedTab != null) {
            state.selectedTab = selectedTab;
        }

        this.setState(state);

        if (this.props.onChange != null) {
            this.props.onChange(value);
        }
    };

    handleOnWidgetAdd = (e) => {
        const parentTheme = this.props.options?.parentTheme();
        const data = WidgetData.getEmpty(this.props.glob, parentTheme);

        data.id = data.cid;

        const hash = _.clone(this.state.hash);

        hash[data.id] = {
            data: data,
            value: data.getDataForSave()
        };

        const value = this.state.value.slice(0);

        value.push(hash[data.id].value);
        this.handleOnChange(value, hash, data.id);

        e.stopPropagation();
        e.preventDefault();
    };

    handleOnTabClick = (id: number) => {
        //LL: 23.08.20:
        //    Here internal Tab Options (Themes) must be re-validate
        //    because appThemes.state is single common object for validation (BrokenValidators)
        //    and it is set by last edited Widget.
        //
        //    Also see in getWidgetTab:
        //      data.widget.isInvalid - it is wrongly set by "theme" prop of last edited Widget for ALL Widgets
        _lastSelectedTab = id;

        this.setState({
            selectedTab: id
        });
    };

    handleOnTabRemove = (id: number) => {
        const removeIndex = _.findIndex(this.state.value, (obj) => {
            return obj.id === id;
        });

        let value = [...this.state.value];

        value.splice(removeIndex, 1);

        const hash = { ...this.state.hash };

        delete hash[id];

        const selectedTabIndex = Math.min(removeIndex, value.length - 1);
        const selectedTab = value[selectedTabIndex]?.id;

        this.handleOnChange(value, hash, selectedTab);
    };

    getWidgetTab = (widget: IWidget) => {
        const item = this.state.hash[widget.id];

        let data: WidgetData;

        if (item != null) {
            data = item.data;
        } else {
            // VR workaround for action "Save" in "Edit JSON" dialog for MultiWidget Container. Here widget.cid is new, but HASH is old = BUG
            const parentTheme = this.props.options?.parentTheme();

            data = WidgetData.getFromObj(widget, this.props.glob, false, parentTheme, this.props.options?.tabID);

            this.state.hash[widget.id] = {
                data: data,
                value: widget
            };
        }

        return (
            <WidgetTab
                key={'widget-tab-' + widget.id}
                data={data}
                selectedTab={this.state.selectedTab}
                allowDelete={this.props.options?.allowDelete}
                glob={this.props.glob}
                onClick={this.handleOnTabClick}
                onRemove={this.handleOnTabRemove}
            />
        );
    };

    refreshValue = (obj: IHashItem) => {
        const value = this.state.value.slice(0);
        const index = _.findIndex(value, (widget) => {
            return widget.id === obj.value.id;
        });

        if (index !== -1) {
            value[index] = obj.value = obj.data.getDataForSave();
        }

        return value;
    };

    setFromData = (id: number, widgetData: WidgetData) => {
        const tab = _.find(this.state.value, (obj) => {
            return (obj.id || obj.cid) == id;
        });

        const tabIndex = _.indexOf(this.state.value, tab);
        const dataObj = widgetData.getDataForSave();
        const value = this.state.value.slice(0);

        value.splice(tabIndex, 1, dataObj);

        delete this.state.hash[id];

        this.state.hash[dataObj.id || dataObj.cid] = {
            data: widgetData,
            value: dataObj
        };

        let selectedTab = this.state.selectedTab;

        if (selectedTab === id) {
            selectedTab = dataObj.id || dataObj.cid;
        }

        this.handleOnChange(value, this.state.hash, selectedTab);
    };

    setFromTemplate = (id: number, data) => {
        if (_.contains(this.props.options && this.props.options.disabledTypes, data.type)) {
            this.props.glob.AppActions.showNotification_Warning(
                'Cannot paste widget of the type ' + data.type + ' into this container',
                null
            );
            return;
        }

        const parentTheme = this.props.options?.parentTheme();
        const res = WidgetData.getFromJSON(JSON.stringify(data), this.props.glob, undefined, parentTheme);

        if (this.addColDefType != null) {
            this.addColDefType.reject();
        }

        if (res.result !== true) {
            return;
        }

        res.widgetData.id = res.widgetData.cid;

        const def = $.Deferred();

        if (res.widgetData.loading != null && res.widgetData.def != null) {
            this.addColDefType = def;
            this.setFromData(id, res.widgetData);

            res.widgetData.def.done(() => {
                def.resolve();
            });

            res.widgetData.def.fail(() => {
                def.reject();
            });
        } else {
            def.resolve();
        }

        def.done(() => {
            this.setFromData(id, res.widgetData);
        });
    };

    handleOnSetWidgetType = (id: number, type: string) => {
        const obj = this.state.hash[id];

        obj.data = obj.data.clone();
        obj.data.setWidgetType(type, false).done(() => {
            this.handleOnChange(this.refreshValue(obj));
        });
    };

    handleOnSetFieldValues = (id: number, type: string, values: IDataEditValues) => {
        const obj = this.state.hash[id];

        obj.data = obj.data.clone();
        obj.data.setFieldValues(type, values);
        this.handleOnChange(this.refreshValue(obj));
    };

    handleOnInternalWidgetChangeTab = (id: number, type: string, iTab: number) => {
        const selTabs = this.state.selectedInternalTabs || {};

        selTabs[id] = iTab;
        this.setState({
            selectedInternalTabs: selTabs
        });
    };

    handleOnSelectFromCollectionClick = () => {
        if (this.addColDef != null) {
            this.addColDef.reject();
        }

        const def = (this.addColDef = $.Deferred());

        this.props.glob.DialogActions.createDialog(
            'Select widget in collection',
            '',
            (id, data) => {
                def.resolve(data);
            },
            () => {},
            () => {},
            'templateDirSelect',
            {
                selectTitle: 'Add Selected Widget',
                rootId: 2,
                selectTemplateMode: true,
                overwriteShow: false,
                allowSelectItem: true
            }
        );

        def.done((data) => {
            this.setFromTemplate(this.state.selectedTab, data);
        });
    };

    handleOnSaveInCollectionClick = () => {
        const obj = this.state.selectedTab && this.state.hash[this.state.selectedTab];

        if (obj == null || obj.data.widget == null || obj.data.widget.isInvalid) {
            showErrorsDialog(obj?.data?.widget, this.props.glob);
            return;
        }

        showSaveInCollectionDialog(obj.data, this.props.glob);
    };

    handleOnPaste = (value: string) => {
        this.tryPaste(value);
    };

    tryPaste = (jsonStr: string) => {
        try {
            const data = JSON.parse(jsonStr);

            Utilities.jsonNormalize_DataSourceNameToID(data, this.props.glob);
            Utilities.jsonNormalize_ThemeNameToID(data, this.props.glob);
            Utilities.renewIDsAndCIDs(data);
            this.setFromTemplate(this.state.selectedTab, data);
        } catch (e) {
            this.props.glob.AppActions.showNotification_Fail('Cannot paste widget data into workspace', null);
        }
    };

    handleOnEditJSONClick = () => {
        const obj = this.state.hash[this.state.selectedTab];

        if (obj == null) {
            return;
        }

        const text = saveToLocalStorage(obj.data, 'pdc_clipboard_editdata', this.props.glob);
        const parentTheme = this.props.options?.parentTheme();

        this.props.glob.DialogActions.createDialog(
            'Edit Widget Configuration as JSON',
            null,
            (obj) => {
                this.tryPaste(obj);
            },
            () => {},
            () => {},
            'custom',
            {
                deny: false,
                confirmTitle: 'Save',
                componentOptions: {
                    theme: parentTheme,
                    dataSourceID: Utilities.getDataSourceID(text)
                },
                resizable: true,
                minHeight: 210,
                Component: EditTargetDialog
            }
        );
    };

    handleOnClipboardCopyClick = () => {
        const obj = this.state.hash[this.state.selectedTab];

        if (obj == null) {
            return;
        }

        saveToLocalStorage(obj.data, 'pdc_clipboard_data', this.props.glob);
    };

    handleOnChangeIcon = (obj: IHashItem, value: string) => {
        obj.data = obj.data.clone();

        if (value == null || value.length === 0) {
            delete obj.value.icon;
            delete obj.data.icon;
        } else {
            obj.value.icon = value;
            obj.data.icon = value;
        }

        const newValue = this.refreshValue(obj);
        const index = _.findIndex(newValue, (widget) => {
            return widget.id === obj.value.id;
        });

        if (index !== -1) {
            newValue[index].iconChanged = true;
        }
        if (this.props.onChange != null) {
            this.props.onChange(newValue);
        }
    };

    handleOnChangeIconHeight = (obj: IHashItem, value: number) => {
        obj.data = obj.data.clone();

        if (value == null || value === 0) {
            cleanOptions(obj, { iconHeight: undefined });
        } else {
            putOptions(obj, { iconHeight: value });
        }

        const newValue = this.refreshValue(obj);

        if (this.props.onChange != null) {
            this.props.onChange(newValue);
        }
    };

    handleOnActivateByDefault = (obj: IHashItem, value: boolean) => {
        obj.data = obj.data.clone();

        if (!value) {
            cleanOptions(obj, { activateByDefault: undefined });
        } else {
            putOptions(obj, { activateByDefault: value });
        }

        const newValue = this.refreshValue(obj);

        if (this.props.onChange != null) {
            this.props.onChange(newValue);
        }
    };

    handleOnChangeBackColor = (obj: IHashItem, value: string) => {
        obj.data = obj.data.clone();

        if (value == null) {
            delete obj.value.tabBackColor;
            delete obj.data.tabBackColor;
        } else {
            obj.value.tabBackColor = value;
            obj.data.tabBackColor = value;
        }

        const newValue = this.refreshValue(obj);
        const index = _.findIndex(newValue, (widget) => {
            return widget.id === obj.value.id;
        });

        if (index !== -1) {
            newValue[index].backColorChanged = true;
        }

        if (this.props.onChange != null) {
            this.props.onChange(newValue);
        }

        this.setState(this.state); //LL to rerender
    };

    handleOnChangeTextColor = (obj: IHashItem, value: string) => {
        obj.data = obj.data.clone();

        if (value == null) {
            delete obj.value.tabTextColor;
            delete obj.data.tabTextColor;
        } else {
            obj.value.tabTextColor = value;
            obj.data.tabTextColor = value;
        }

        const newValue = this.refreshValue(obj);
        const index = _.findIndex(newValue, (widget) => {
            return widget.id === obj.value.id;
        });

        if (index !== -1) {
            newValue[index].textColorChanged = true;
        }

        if (this.props.onChange != null) {
            this.props.onChange(newValue);
        }

        this.setState(this.state);
    };

    handleOnHeaderTemplateChange = (obj: IHashItem, value: string) => {
        obj.data = obj.data.clone();

        if (value == null || value === '') {
            cleanOptions(obj, { headerTemplate: undefined });
        } else {
            putOptions(obj, { headerTemplate: value });
        }

        const newValue = this.refreshValue(obj);

        if (this.props.onChange != null) {
            this.props.onChange(newValue);
        }

        this.setState(this.state);
    };

    render() {
        const { options } = this.props;

        let multiWidgetFieldTab: React.JSX.Element;

        const obj = this.state.hash[this.state.selectedTab];

        if (obj != null) {
            const selectedInternalTab = this.state.selectedInternalTabs?.[obj.data.id] ?? 0;
            const parentTheme = options?.parentTheme();

            multiWidgetFieldTab = (
                <MultiWidgetFieldTab
                    obj={obj}
                    values={this.props.values}
                    parameterProps={this.props.parameterProps}
                    paramOptions={this.props.paramOptions}
                    tabID={options.tabID}
                    disabledTypes={options.disabledTypes}
                    parentTheme={parentTheme}
                    hideType={this.props.hideType}
                    hideTabs={this.props.hideTabs}
                    selectedInternalTab={selectedInternalTab}
                    showTabIcons={this.props.tabIcons ?? options.tabIcons}
                    showTabBackgroundColor={this.props.tabBackColors ?? options.tabBackColors}
                    defaultTabBackgroundColor={
                        this.props.defaultTabBackgroundColor ?? options.defaultTabBackgroundColor
                    }
                    showTabTextColors={this.props.tabTextColors ?? options.tabTextColors}
                    defaultTabTextColor={this.props.defaultTabTextColor ?? options.defaultTabTextColor}
                    showActivateByDefault={this.props.showActivateByDefault ?? options.showActivateByDefault}
                    tabsFieldName={options.tabsFieldName}
                    showHeaderTemplate={this.props.showHeaderTemplate ?? options.showHeaderTemplate}
                    glob={this.props.glob}
                    onSetWidgetType={this.handleOnSetWidgetType}
                    onSaveInCollectionClick={this.handleOnSaveInCollectionClick}
                    onClipboardCopyClick={this.handleOnClipboardCopyClick}
                    onEditJSONClick={this.handleOnEditJSONClick}
                    onSelectFromCollectionClick={this.handleOnSelectFromCollectionClick}
                    onPaste={this.handleOnPaste}
                    onChangeIcon={this.handleOnChangeIcon}
                    onChangeIconHeight={this.handleOnChangeIconHeight}
                    onChangeBackColor={this.handleOnChangeBackColor}
                    onChangeTextColor={this.handleOnChangeTextColor}
                    onChangeActivateByDefault={this.handleOnActivateByDefault}
                    onSetFieldValues={this.handleOnSetFieldValues}
                    onInternalWidgetChangeTab={this.handleOnInternalWidgetChangeTab}
                    onHeaderTemplateChange={this.handleOnHeaderTemplateChange}
                />
            );
        }

        const tabContent = (
            <div className="tab-pane active" key={this.state.selectedTab}>
                {multiWidgetFieldTab}
            </div>
        );

        let content: React.JSX.Element;

        if (this.props.noTabs) {
            content = tabContent;
        } else {
            const tabs = _.map(this.state.value, this.getWidgetTab);

            if (tabs == null || options.maxWidgets == null || tabs.length < options.maxWidgets) {
                tabs.push(
                    <li key="_add-tab" className="nav-item add-widget-tab">
                        <a className="nav-link" onClick={this.handleOnWidgetAdd}>
                            <span className="glyphicon glyphicon-file"></span>
                            <em>{'Add'}</em>
                        </a>
                    </li>
                );
            }

            content = (
                <>
                    <ul
                        ref={(input) => {
                            this.references.tabs = input;
                        }}
                        className="nav nav-tabs widget-tabs"
                    >
                        {tabs}
                        <li key={'widget-tab'} className="widget-tab ui-sortable-placeholder"></li>
                    </ul>
                    <div className="tab-content">{tabContent}</div>
                </>
            );
        }

        return <div className="multi-widget-field">{content}</div>;
    }
}

function WidgetTab(props: {
    data: WidgetData;
    selectedTab: number;
    allowDelete: boolean;
    glob: IGlob;
    onClick(id: number): void;
    onRemove(id: number): void;
}) {
    const { data } = props;

    let content: React.JSX.Element;

    if (data.type == null) {
        content = <em>{'No Type'}</em>;
    } else {
        content = <span>{data.values[data.type]?.name}</span>;
    }

    let errorMessage: React.JSX.Element;

    if (data.widget?.isInvalid) {
        errorMessage = <span className="glyphicon glyphicon-warning-sign" style={{ color: 'red' }}></span>;
    }

    let deleteIcon: React.JSX.Element;

    if (props.allowDelete !== false) {
        deleteIcon = (
            <span
                className="glyphicon glyphicon-remove"
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    props.onRemove?.(data.id);
                }}
            ></span>
        );
    }

    return (
        <li key={data.id} className="nav-item widget-tab">
            <a
                className={'nav-link' + (data.id === props.selectedTab ? ' active' : '')}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    props.onClick?.(data.id);
                }}
            >
                {errorMessage}
                {deleteIcon}
                {content}
            </a>
        </li>
    );
}
